import {
  BannerWrapper,
  CloudinaryImageSrcProps,
  DefaultContentfulBannerWrapperProps,
  ITrackingProps
} from '@vp/banner-wrapper'
import React, { useCallback, useEffect, useState } from 'react'
import { BannerWithLeftHandNavigationBelowBanner, BoundedContent } from '@vp/swan'

import { getPageZone } from './StandardHeroContent/StandardHeroContent'
import StandardHeroUploadFlowCTA from './StandardHeroContent/StandardHeroUploadFlowCTA'
import ErrorBoundary from './ErrorBoundary'

import { SidebarType } from '../types/TemplateConfiguration'
import { CtaSkin } from '../types/StandardHeroContent'

import { useAppContext } from '../state/AppContext'
import { trackClickEvent } from '../utils/tracking/tracking'

export interface BannerProps {
  bannerProps: DefaultContentfulBannerWrapperProps[]
  headerType: string
  sectionNumber?: number
  sidebar?: SidebarType
  sidebarEnabled?: boolean
  leftNav?: React.JSX.Element
  useH1ForTitle?: boolean
}

const CustomBannerWrapper = (props: BannerProps) => {
  const { auth, locale, logger, debugMode } = useAppContext()
  const [isSignedIn, setIsSignedIn] = useState<boolean | undefined>(undefined)
  const [customerId, setCustomerId] = useState<string | undefined>(undefined)

  const {
    bannerProps,
    headerType,
    sectionNumber,
    sidebar,
    sidebarEnabled,
    leftNav,
    useH1ForTitle
  } = props

  useEffect(() => {
    if (auth) {
      setIsSignedIn(auth.isSignedIn())
      const profile = auth.getProfile()
      setCustomerId(profile?.['https://claims.cimpress.io/canonical_id'])
    }
  }, [auth])

  const pageZone =
    headerType === ('Side by Side Hero' as string)
      ? 'Side by Side Hero'
      : getPageZone(headerType, sectionNumber)

  const { merchandisingCategoryId, uploadFlowMpv } = useAppContext()

  bannerProps.forEach((banner) => {
    const updateAltText = (image: CloudinaryImageSrcProps) => {
      if (image && !image.altTextOverride) {
        image.altTextOverride = ''
      }
    }

    if (Array.isArray(banner.bannerImage)) {
      banner.bannerImage.forEach((image) =>
        updateAltText(image.imageSrc as CloudinaryImageSrcProps)
      )
    } else {
      updateAltText(banner.bannerImage.imageSrc as CloudinaryImageSrcProps)
    }
  })

  const ctaButtonComponent = useCallback(
    (skin: CtaSkin, uploadFlowMpvId: string) => {
      if (uploadFlowMpvId && uploadFlowMpv && uploadFlowMpv.mpvId === uploadFlowMpvId) {
        return (
          <StandardHeroUploadFlowCTA
            key="upload-flow-cta"
            pageZone={pageZone}
            uploadFlowMpv={uploadFlowMpv}
            skin={skin}
          />
        )
      }
      return null
    },
    [uploadFlowMpv, pageZone]
  )

  const trackBannerClickEvent = (
    event: React.MouseEvent<Element, MouseEvent>,
    { destinationUrl, ctaPosition }: ITrackingProps
  ) => {
    trackClickEvent({
      categoryId: merchandisingCategoryId,
      pageZone,
      ctaValue: `CTA ${ctaPosition}`,
      destinationUrl: destinationUrl ?? ' '
    })
  }

  if (sidebarEnabled && sidebar) {
    return (
      <BoundedContent paddingX={0} marginBottom={{ xs: 7, sm: 7, md: 8 }}>
        <ErrorBoundary
          logger={logger}
          debug={debugMode}
          context={{
            component: 'BannerWrapper',
            sidebarEnabled: true,
            bannerProps,
            merchandisingCategoryId,
            locale
          }}
        >
          <BannerWrapper
            bannerProps={bannerProps}
            trackingHandler={trackBannerClickEvent}
            leftNav={leftNav}
            extraProps={{
              useH1ForTitle
            }}
            isSignedIn={isSignedIn}
            customerId={customerId}
            locale={locale}
            gmpvFlowFunction={ctaButtonComponent}
          />
        </ErrorBoundary>
      </BoundedContent>
    )
  }
  const bannerContent = (
    <ErrorBoundary
      logger={logger}
      debug={debugMode}
      context={{
        component: 'BannerWrapper',
        sidebarEnabled: false,
        bannerProps,
        merchandisingCategoryId,
        locale
      }}
    >
      <BannerWrapper
        bannerProps={bannerProps}
        trackingHandler={trackBannerClickEvent}
        gmpvFlowFunction={ctaButtonComponent}
        extraProps={{
          useH1ForTitle
        }}
        isSignedIn={isSignedIn}
        customerId={customerId}
        locale={locale}
      />
    </ErrorBoundary>
  )
  return (
    <BoundedContent paddingX={0} marginBottom={{ xs: 7, sm: 7, md: 8 }}>
      {sidebarEnabled ? (
        <BannerWithLeftHandNavigationBelowBanner data-testid="bannerWithSideNav">
          {bannerContent}
        </BannerWithLeftHandNavigationBelowBanner>
      ) : (
        bannerContent
      )}
    </BoundedContent>
  )
}

export default CustomBannerWrapper
